import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Printer,
  Volume2,
  X,
  ArrowRight,
  Sun,
  Moon,
} from "lucide-react";
import {
  ALPHABET_GROUPS,
  ALL_LETTERS,
  letterForms,
  NON_CONNECTORS,
  VOWEL_SIGNS,
  PRONOUNS,
  POSSESSIVES,
  PHRASES,
  NUMBERS,
  SUN_LETTERS,
  MOON_LETTERS,
  VERB_KATABA,
  ROOT_PATTERNS,
} from "@/data/arabicContent";
import { PublicNav, PublicFooter, BRAND } from "@/components/public/PublicChrome";

/* =============================================================================
 * FICHES MÉMO PUBLIQUES — /fr/fiches
 *
 * Une seule fiche est affichée à la fois : la page reste courte, on ne fait
 * jamais défiler pour rien. La barre de catégories est collante sous la navbar
 * et défile horizontalement au doigt sur téléphone.
 * ========================================================================== */

const TABS = [
  { id: "alphabet", label: "Alphabet" },
  { id: "voyelles", label: "Voyelles et signes" },
  { id: "pronoms", label: "Pronoms" },
  { id: "phrases", label: "Phrases utiles" },
  { id: "nombres", label: "Nombres" },
  { id: "article", label: "L'article" },
  { id: "conjugaison", label: "Conjugaison" },
  { id: "racines", label: "Racines" },
];

function speak(text) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "ar-SA";
  u.rate = 0.75;
  window.speechSynthesis.speak(u);
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white ${className}`}>
      {children}
    </div>
  );
}

function Row({ ar, tr, fr, onPlay }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="arabic text-2xl leading-none shrink-0" dir="rtl">
        {ar}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-slate-900 truncate">
          {fr}
        </span>
        <span className="block text-xs italic text-slate-500 truncate">{tr}</span>
      </span>
      <button
        onClick={() => onPlay(ar)}
        aria-label={`Écouter ${tr}`}
        className="shrink-0 grid place-items-center w-8 h-8 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition print:hidden"
      >
        <Volume2 size={15} />
      </button>
    </li>
  );
}

export default function PublicCheatsheets() {
  const [tab, setTab] = useState("alphabet");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null); // lettre sélectionnée

  useEffect(() => {
    document.title = "Fiches mémo d'arabe — Harf";
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_LETTERS;
    return ALL_LETTERS.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.sound.toLowerCase().includes(q) ||
        l.char === q ||
        l.wordTranslit.toLowerCase().includes(q) ||
        l.wordFr.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      <style>{`
        @media print {
          header, footer, .print\\:hidden { display: none !important; }
          .print-sheet { padding: 0 !important; }
        }
      `}</style>

      <PublicNav />

      {/* En-tête compact */}
      <div className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 pt-8 pb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold">
              Fiches mémo
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Vérifier une règle en dix secondes, sans reprendre une leçon.
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
            >
              <Printer size={15} /> Imprimer
            </button>
            <Link
              to="/fr/alphabet"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition hover:opacity-90"
              style={{ background: BRAND }}
            >
              S'entraîner <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </div>

      {/* Onglets collants */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-slate-200 print:hidden">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto py-3 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setTab(t.id);
                  setQuery("");
                }}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                  tab === t.id
                    ? "text-white border-transparent"
                    : "border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600"
                }`}
                style={tab === t.id ? { background: BRAND } : undefined}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 sm:py-8 print-sheet">
        {/* ------------------------------------------------------- ALPHABET */}
        {tab === "alphabet" && (
          <>
            <div className="relative mb-5 print:hidden">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Chercher une lettre, un son, un mot…"
                className="w-full pl-11 pr-4 py-3 rounded-2xl border border-slate-200 text-[15px] outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <p className="text-sm text-slate-500 mb-4">
              Touche une lettre pour voir ses quatre formes et un mot d'exemple.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filtered.map((l) => (
                <button
                  key={l.char}
                  onClick={() => setOpen(l)}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-blue-300 hover:shadow-sm transition"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="arabic text-4xl leading-none" dir="rtl">
                      {l.char}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {l.groupLabel}
                    </span>
                  </div>
                  <div className="mt-3 font-semibold text-sm">{l.name}</div>
                  <div className="text-xs text-slate-500">son « {l.sound} »</div>
                </button>
              ))}
            </div>

            {filtered.length === 0 && (
              <p className="text-center text-slate-500 py-12">
                Aucune lettre ne correspond. Essaie « ba », « th » ou « livre ».
              </p>
            )}

            {/* Version imprimable, sous forme de tableau */}
            <div className="hidden print:block mt-6">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 border-b">Lettre</th>
                    <th className="text-left p-2 border-b">Nom</th>
                    <th className="text-left p-2 border-b">Son</th>
                    <th className="text-left p-2 border-b">Exemple</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_LETTERS.map((l) => (
                    <tr key={l.char}>
                      <td className="p-2 border-b arabic text-2xl">{l.char}</td>
                      <td className="p-2 border-b">{l.name}</td>
                      <td className="p-2 border-b">{l.sound}</td>
                      <td className="p-2 border-b">
                        <span className="arabic text-lg">{l.word}</span> —{" "}
                        {l.wordFr}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ------------------------------------------------------- VOYELLES */}
        {tab === "voyelles" && (
          <>
            <p className="text-sm text-slate-600 mb-5 max-w-2xl">
              L'arabe n'écrit pas les voyelles courtes dans les textes courants.
              Elles apparaissent sous forme de petits signes au-dessus ou en
              dessous de la consonne, surtout dans les livres d'apprentissage.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {VOWEL_SIGNS.map((v) => (
                <Card key={v.name} className="p-4 flex items-center gap-4">
                  <span className="arabic text-4xl leading-none w-14 text-center shrink-0" dir="rtl">
                    {v.sign}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{v.name}</div>
                    <div className="text-xs text-blue-600 font-medium">
                      se lit « {v.read} »
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{v.note}</p>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* -------------------------------------------------------- PRONOMS */}
        {tab === "pronoms" && (
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <h2 className="px-4 py-3 border-b border-slate-200 font-bold text-sm">
                Pronoms personnels
              </h2>
              <ul className="divide-y divide-slate-100">
                {PRONOUNS.map((p) => (
                  <Row key={p.tr} ar={p.ar} tr={p.tr} fr={p.fr} onPlay={speak} />
                ))}
              </ul>
            </Card>
            <Card>
              <h2 className="px-4 py-3 border-b border-slate-200 font-bold text-sm">
                Possession : le suffixe s'attache au nom
              </h2>
              <ul className="divide-y divide-slate-100">
                {POSSESSIVES.map((p) => (
                  <Row key={p.tr} ar={p.ar} tr={p.tr} fr={p.fr} onPlay={speak} />
                ))}
              </ul>
              <p className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
                Il n'y a pas de mot séparé pour « mon » ou « ton » : la marque
                se colle à la fin du nom.
              </p>
            </Card>
          </div>
        )}

        {/* -------------------------------------------------------- PHRASES */}
        {tab === "phrases" && (
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <ul className="divide-y divide-slate-100">
                {PHRASES.slice(0, 6).map((p) => (
                  <Row key={p.tr} ar={p.ar} tr={p.tr} fr={p.fr} onPlay={speak} />
                ))}
              </ul>
            </Card>
            <Card>
              <ul className="divide-y divide-slate-100">
                {PHRASES.slice(6).map((p) => (
                  <Row key={p.tr} ar={p.ar} tr={p.tr} fr={p.fr} onPlay={speak} />
                ))}
              </ul>
            </Card>
          </div>
        )}

        {/* -------------------------------------------------------- NOMBRES */}
        {tab === "nombres" && (
          <>
            <p className="text-sm text-slate-600 mb-5 max-w-2xl">
              Les chiffres dits « arabes » utilisés en Orient s'écrivent de
              gauche à droite, comme en français, même dans un texte qui se lit
              de droite à gauche.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {NUMBERS.map((n) => (
                <Card key={n.fr} className="p-4 flex items-center gap-3">
                  <span className="arabic text-3xl w-10 text-center shrink-0">
                    {n.digit}
                  </span>
                  <div className="min-w-0">
                    <div className="arabic text-lg leading-tight" dir="rtl">
                      {n.ar}
                    </div>
                    <div className="text-xs italic text-slate-500">{n.tr}</div>
                  </div>
                  <span className="ml-auto font-head font-extrabold text-slate-300">
                    {n.fr}
                  </span>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* -------------------------------------------------------- ARTICLE */}
        {tab === "article" && (
          <div className="space-y-4">
            <Card className="p-5">
              <h2 className="font-bold mb-2">Un seul article : الـ (al-)</h2>
              <p className="text-sm text-slate-600">
                Il se colle devant le nom.{" "}
                <span className="arabic text-lg">كِتاب</span> kitâb, un livre →{" "}
                <span className="arabic text-lg">الكِتاب</span> al-kitâb, le
                livre. Il n'y a pas d'article indéfini : l'absence de الـ
                suffit.
              </p>
            </Card>
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sun size={17} className="text-amber-500" />
                  <h3 className="font-bold text-sm">
                    Lettres solaires : le « l » disparaît
                  </h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  <span className="arabic text-lg">الشَّمس</span> s'écrit
                  al-shams mais se prononce <strong>ash-shams</strong>.
                </p>
                <div className="flex flex-wrap gap-1.5" dir="rtl">
                  {SUN_LETTERS.map((c) => (
                    <span
                      key={c}
                      className="arabic text-xl w-9 h-9 grid place-items-center rounded-lg bg-amber-50 border border-amber-200"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </Card>
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Moon size={17} className="text-blue-600" />
                  <h3 className="font-bold text-sm">
                    Lettres lunaires : le « l » se prononce
                  </h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  <span className="arabic text-lg">القَمَر</span> se prononce
                  bien <strong>al-qamar</strong>.
                </p>
                <div className="flex flex-wrap gap-1.5" dir="rtl">
                  {MOON_LETTERS.map((c) => (
                    <span
                      key={c}
                      className="arabic text-xl w-9 h-9 grid place-items-center rounded-lg bg-blue-50 border border-blue-200"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------- CONJUGAISON */}
        {tab === "conjugaison" && (
          <>
            <p className="text-sm text-slate-600 mb-5 max-w-2xl">
              Le passé se construit avec des terminaisons, le présent avec des
              préfixes. Modèle :{" "}
              <span className="arabic text-lg">كَتَبَ</span> kataba, écrire,
              racine {VERB_KATABA.root}.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: "Passé (accompli)", rows: VERB_KATABA.past },
                { title: "Présent (inaccompli)", rows: VERB_KATABA.present },
              ].map((t) => (
                <Card key={t.title}>
                  <h2 className="px-4 py-3 border-b border-slate-200 font-bold text-sm">
                    {t.title}
                  </h2>
                  <ul className="divide-y divide-slate-100">
                    {t.rows.map((r) => (
                      <li
                        key={r.tr}
                        className="flex items-center gap-3 px-4 py-2.5"
                      >
                        <span className="arabic text-base w-14 text-slate-500 shrink-0" dir="rtl">
                          {r.p}
                        </span>
                        <span className="arabic text-xl flex-1" dir="rtl">
                          {r.ar}
                        </span>
                        <span className="text-xs italic text-slate-500 w-24 text-right">
                          {r.tr}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* --------------------------------------------------------- RACINES */}
        {tab === "racines" && (
          <>
            <p className="text-sm text-slate-600 mb-5 max-w-2xl">
              Presque tous les mots arabes reposent sur une racine de trois
              consonnes. En la reconnaissant, tu devines le sens d'un mot que tu
              n'as jamais vu. Exemple avec ك ت ب, l'idée d'écrire.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ROOT_PATTERNS.map((p) => (
                <Card key={p.pattern} className="p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="arabic text-2xl" dir="rtl">
                      {p.pattern}
                    </span>
                    <span className="text-[11px] text-slate-500">{p.role}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-baseline gap-2">
                    <span className="arabic text-2xl" dir="rtl">
                      {p.ex}
                    </span>
                    <span className="text-xs italic text-slate-500">{p.tr}</span>
                    <span className="ml-auto text-sm font-medium">{p.fr}</span>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Rappel d'entraînement, discret et en fin de fiche */}
        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-5 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left print:hidden">
          <div className="flex-1">
            <p className="font-semibold">Lire une fiche ne suffit pas.</p>
            <p className="text-sm text-slate-600">
              Teste-toi sur l'alphabet, puis crée un compte pour garder tes
              résultats.
            </p>
          </div>
          <Link
            to="/fr/alphabet"
            className="w-full sm:w-auto px-5 py-3 rounded-xl text-white font-semibold text-center"
            style={{ background: BRAND }}
          >
            Lancer un test
          </Link>
        </div>
      </main>

      {/* Fiche détaillée d'une lettre */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Fiche de la lettre ${open.name}`}
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="arabic text-6xl leading-none" dir="rtl">
                  {open.char}
                </div>
                <div className="mt-3 font-head text-xl font-bold">{open.name}</div>
                <div className="text-sm text-slate-500">son « {open.sound} »</div>
              </div>
              <button
                onClick={() => setOpen(null)}
                aria-label="Fermer"
                className="grid place-items-center w-9 h-9 rounded-full border border-slate-200 text-slate-500"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-6">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
                Les quatre formes
              </h3>
              <div className="grid grid-cols-4 gap-2" dir="rtl">
                {(() => {
                  const f = letterForms(open.char);
                  return [
                    { k: "isolated", label: "isolée" },
                    { k: "initial", label: "début" },
                    { k: "medial", label: "milieu" },
                    { k: "final", label: "fin" },
                  ].map((x) => (
                    <div
                      key={x.k}
                      className="rounded-xl border border-slate-200 py-3 text-center"
                    >
                      <div className="arabic text-2xl leading-none">{f[x.k]}</div>
                      <div className="text-[10px] text-slate-400 mt-1.5" dir="ltr">
                        {x.label}
                      </div>
                    </div>
                  ));
                })()}
              </div>
              {NON_CONNECTORS.includes(open.char) && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Cette lettre ne se lie jamais à la lettre suivante : le mot
                  s'interrompt visuellement après elle.
                </p>
              )}
            </div>

            <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
                Mot d'exemple
              </h3>
              <div className="flex items-center gap-3">
                <span className="arabic text-3xl" dir="rtl">
                  {open.word}
                </span>
                <span className="text-sm">
                  <span className="block font-medium">{open.wordFr}</span>
                  <span className="block text-xs italic text-slate-500">
                    {open.wordTranslit}
                  </span>
                </span>
                <button
                  onClick={() => speak(open.word)}
                  aria-label="Écouter le mot"
                  className="ml-auto grid place-items-center w-9 h-9 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                >
                  <Volume2 size={16} />
                </button>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-600">
              <strong className="text-slate-900">À retenir :</strong> {open.note}
            </p>

            <Link
              to="/fr/alphabet"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl text-white font-semibold"
              style={{ background: BRAND }}
            >
              S'entraîner sur cette famille <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
}
